import { useState, useCallback, useMemo } from "react"

import FieldLookupValues from "./FieldLookupValues"
import { getFieldFromFormValue, getKindField } from "./helpers"
import { NUMERIC_FIELD_KINDS } from "web/constants"
import { Button, Form, ViewOrEditField, InfoMessage } from "web/components"
import Label from "web/components/Form/Controls/Label"

import { useModals, useDialogModal, Dialogs } from "web/hooks/modals"

import {
  isNotAValue,
  valueFromPlainObject,
  plainObjectFromValue,
} from "web/services/helpers"
import { dateFormatOptions } from "web/services/format"

import formSchema from "./schema.json"

function setHelperFieldValues(value) {
  return {
    ...value,
    isCalculated: !isNotAValue(value.expression),
    isConditionallyShown: !isNotAValue(value.show_expression),
    dateFormat: value.kind === "Date" ? value.format : null,
    numericalFormat: value.kind === "Money" ? value.format : null,
    numberFormat: ["Number", "Area", "Length", "Distance"].includes(value.kind)
      ? value.format
      : null,
    multiple_values: value.multiple_values || false,
    convert_for_display: value.convert_for_display === false ? false : true,
    prioritize_default_expression: value.prioritize_default_expression || false,
  }
}

function setWeightFieldLookupValues(fields) {
  const numericFields = fields.filter((f) =>
    NUMERIC_FIELD_KINDS.includes(f.kind)
  )
  const weightFieldIndex = formSchema.findIndex(
    (f) => f.key === "weight_field_id"
  )

  formSchema[weightFieldIndex].lookup_values = numericFields.map((f) => ({
    value: f.id,
    label: f.label,
  }))
}

function setDateFormatLookupValues() {
  const dateFormatIndex = formSchema.findIndex((f) => f.key === "dateFormat")

  formSchema[dateFormatIndex].lookup_values = dateFormatOptions()
}

function getFormFields(fields, field) {
  setWeightFieldLookupValues(fields)
  setDateFormatLookupValues()

  return [getKindField(field), ...formSchema]
}

function ShowField({
  fields,
  field: fieldFromProps,
  respond,
  onWaitingChanged,
}) {
  const [field, setField] = useState(fieldFromProps)
  const [submissionFailed, setSubmissionFailed] = useState()

  const isCreate = field.id === undefined

  const formFields = useMemo(
    () => getFormFields(fields, field),
    [fields, field]
  )

  const encodedValue = valueFromPlainObject(
    setHelperFieldValues(field),
    formFields
  )

  const [formValue, setFormValue] = useState(encodedValue)

  const confirmDeleteModal = useDialogModal(Dialogs.ConfirmDelete("field"))
  const dialogModal = useDialogModal()

  const fieldIsNumeric = [
    "Date",
    "Number",
    "Money",
    "Area",
    "Length",
    "Distance",
  ].includes(formValue.kind.value)

  const fieldIsSummarizable = [
    "Number",
    "Money",
    "Area",
    "Length",
    "Distance",
  ].includes(formValue.kind.value)

  const fieldIsConvertable = ["Money", "Area", "Length", "Distance"].includes(
    formValue.kind.value
  )

  const fieldCanBeCalculated = formValue.kind.value != "RichText"

  const isNewLookupField =
    formValue.kind && formValue.kind.value === "Lookup" && isCreate

  const isCustom = field.customization_level === "custom"
  const isCustomized = field.customization_level === "customized"

  // Name is not an attribute, but should follow the is_attribute rules for editing.
  const isSystemField =
    field.is_attribute || ["deal_name", "asset_name"].includes(field.key)

  const FormField = useCallback(
    ({ fieldKey, ...rest }) => {
      const formField = formFields.find((el) => el.key === fieldKey)

      const allowModification =
        (!isSystemField && formField.mutable) || formField.key === "label"

      const canBeModified = isCreate || allowModification

      const isBooleanField = formField.kind === "Boolean"

      return (
        <ViewOrEditField
          fieldKey={fieldKey}
          mode={canBeModified ? "edit" : "view"}
          readOnly={canBeModified ? false : true}
          includeNullSuggestion={isBooleanField ? false : undefined}
          {...rest}
        />
      )
    },
    [isCreate, isSystemField, formFields]
  )

  async function handleDelete() {
    const dialogResult = await (isCustom
      ? confirmDeleteModal()
      : dialogModal({
          title: "Revert Field",
          prompt:
            "Are you sure you want to revert this field to the standard definition?",
          commands: [
            { label: "Revert", variant: "primary_danger", response: true },
          ],
        }))
    if (dialogResult && dialogResult.response) {
      await field.destroy()
      respond()
    }
  }

  async function handleSaveNewLookup() {
    setField(
      await fields.add(
        getFieldFromFormValue({
          formValue: plainObjectFromValue(formValue),
          fields,
        })
      )
    )
  }

  async function handleSubmit(value) {
    onWaitingChanged(true)
    const fieldValue = getFieldFromFormValue({
      formValue: plainObjectFromValue(value),
      fields,
    })

    try {
      if (isCreate) {
        await fields.add(fieldValue)
      } else {
        await field.update(fieldValue)
      }

      respond()
    } catch (e) {
      setSubmissionFailed(true)
    }
    onWaitingChanged(false)
  }

  return (
    <Form
      value={formValue}
      onChange={setFormValue}
      onSubmit={handleSubmit}
      fields={formFields}
      render={(form) => (
        <div className="edit-field h-full">
          <div className="modal-content-above-commands">
            {isSystemField && (
              <InfoMessage title="System Field" className="mb-3">
                This field's value is set by Origin. Only the field's name can
                be customized.
              </InfoMessage>
            )}
            <div className="grid gap-y-2.5">
              {submissionFailed && (
                <div className="mb-2 text-sm italic text-red-500">
                  There was a problem with your field. Please try again.
                </div>
              )}
              <FormField fieldKey="label" autoFocus={true} />
              <FormField fieldKey="kind" />
              <FormField fieldKey="basis_kind" />
              <div className="[&_.truncate]:whitespace-normal">
                <Label
                  label="Description"
                  description="Description of the field that is displayed in a tooltip when someone clicks on the field's label."
                />
                <FormField fieldKey="description" rows="3" hideLabel={true} />
              </div>
              {isNewLookupField && (
                <div className="text-sm">
                  New field must be saved before field options can be
                  edited.&nbsp;
                  <span
                    className="cursor-pointer text-primary-4 hover:text-feature-6"
                    onClick={handleSaveNewLookup}
                  >
                    Save Field
                  </span>
                </div>
              )}
              {fieldIsNumeric && (
                <div>
                  <FormField fieldKey="dateFormat" sortSuggestions={false} />
                  <FormField fieldKey="numberFormat" />
                  <FormField fieldKey="numericalFormat" />
                </div>
              )}
              {field.kind === "Lookup" && (
                <FieldLookupValues field={field} readOnly={isSystemField} />
              )}
              <FormField fieldKey="multiple_values" />
              <FormField fieldKey="default_expression" />
              <div className="pl-8 empty:hidden">
                <FormField fieldKey="prioritize_default_expression" />
              </div>
              <div className="grid gap-y-2.5 rounded-md border p-2">
                <div className="font-bold">Advanced Options</div>

                {fieldCanBeCalculated && (
                  <>
                    <FormField fieldKey="isCalculated" />
                    <div className="pl-8 empty:hidden">
                      <FormField fieldKey="expression" />
                    </div>
                  </>
                )}

                <FormField fieldKey="isConditionallyShown" />
                <div className="pl-8 empty:hidden">
                  <FormField fieldKey="show_expression" />
                </div>

                {fieldIsConvertable && (
                  <>
                    <FormField fieldKey="convert_for_display" />
                    <div className="pl-8 empty:hidden">
                      <FormField fieldKey="display_unit_code" />
                    </div>
                  </>
                )}

                {fieldIsSummarizable && (
                  <>
                    <FormField fieldKey="summary_method" />
                    <div className="pl-8 empty:hidden">
                      <FormField fieldKey="weight_field_id" />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="modal-commands">
            <div className="right-buttons">
              <Button disabled={!form.isValid} onClick={form.submit}>
                Save
              </Button>
            </div>
            {!isCreate && isCustomized && (
              <div className="right-buttons">
                <Button variant="primary_danger" onClick={handleDelete}>
                  {isCustom ? "Delete" : "Revert to Standard Field"}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    ></Form>
  )
}

export default function useShowFieldModal() {
  const { showModal } = useModals()

  return async function ({ fields, field, isNew = false }) {
    return await showModal({
      title: isNew ? "Add Field" : "View/Edit Field",
      fullScreen: false,
      widthPixels: 800,
      message: "Updating Fields. Please wait",
      component: ({ respond, onWaitingChanged }) => (
        <ShowField
          fields={fields}
          field={field}
          isNew={isNew}
          respond={respond}
          onWaitingChanged={onWaitingChanged}
        />
      ),
    })
  }
}
